import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CreateArrayOptions, ROLE_SLUG } from '@/constants';
import {
  UpdateTransportFormValues,
  UpdateTransportModalData
} from '@/constants/Interfaces/CommonInterfaces';
import { FormSelect, FormTextarea } from '@/components/Common/FormFields';
import useCommonStore from '@/stores/common';
import { toYyyyMmDd } from '@/utils/common';
import { updateTransportModalSchema } from '@/constants/ValidationSchema/FormSchema';
import {
  useGetCarsListQuery,
  useUpdateTransportRosterMutation
} from '@/services/transport.services';
import { useQueryClient } from '@tanstack/react-query';
import { useCheckCarAvailabilityMutation } from '@/services/lesson.service';
import CustomConfirmModal from '@/components/Common/Modals/CustomConfirmModal';
import { useTransportRosterStore } from '@/stores/transportRoster';
import { Link } from 'lucide-react';
import { useAuthStore } from '@/stores';
import InstructorTransportView from './InstructorTransportView';
const commonPayload = 'pagination=false';

const UpdateTransportModal: React.FC = () => {
  const { hideCommonModal, modalConfig } = useCommonStore();
  const queryClient = useQueryClient();
  const { data: carListData } = useGetCarsListQuery(commonPayload);
  const { mutateAsync: updateTransportRosterMutation } =
    useUpdateTransportRosterMutation();
  const { mutateAsync: checkCarAvailabilityMutation } =
    useCheckCarAvailabilityMutation();
  const { selectedDate: rosterSelectedDate } = useTransportRosterStore();

  const userInfo = useAuthStore(state => state.userInfo);
  const roleData = userInfo?.user?.role as unknown;
  const roleSlug =
    (typeof roleData === 'object' && roleData !== null
      ? (roleData as { slug?: string }).slug
      : null) ?? undefined;
  const isInstructor = roleSlug === ROLE_SLUG?.INSTRUCTOR;

  const data =
    (modalConfig?.data as unknown as UpdateTransportModalData) ||
    ({
      lesson: {
        id: 0,
        client_name: '',
        number_in_party: 0,
        pickup_time: '',
        return_time: '',
        pickup_end_time: '',
        start_time: '',
        end_time: '',
        instructor: { id: 0, name: '' },
        hotel: { id: 0, name: '' },
        mountain: { id: 0, name: '' },
        schedule_slots: [],
        instructor_name: '',
        hotel_name: '',
        mountain_name: '',
        transport_comments: '',
        transport_pax: null
      },
      cars: []
    } as UpdateTransportModalData);
  const lessonStart = data.lesson.start_time;
  // const lessonEnd = data.lesson.end_time; // change request by client 2026-01-12

  const pickupTimeOptions = useMemo(() => {
    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    const toMinutes = (t: string) => {
      if (!t || !/^\d{2}:\d{2}$/.test(t)) return 0;
      const [hh, mm] = t.split(':').map(Number);
      return hh * 60 + mm;
    };
    const format = (mins: number) =>
      `${pad(Math.floor(mins / 60))}:${pad(mins % 60)}`;

    const START_MIN = 7 * 60; // 07:00
    // If lessonStart is invalid or missing, fallback to 22:00 to show full range or restricted default
    // Logic from AddLesson: use startTime to limit upper bound.
    const endTimeMin =
      typeof lessonStart === 'string' && /^\d{2}:\d{2}$/.test(lessonStart)
        ? toMinutes(lessonStart)
        : 22 * 60;

    // Pickup must be BEFORE the lesson start.
    // AddLesson uses (endTime - 15) as the last slot.
    const lastSlot = endTimeMin; // allow pickup time to be same as start time

    if (lastSlot < START_MIN) return [];

    const opts: Array<{ value: string; label: string }> = [];
    for (let m = START_MIN; m <= lastSlot; m += 15) {
      const t = format(m);
      opts.push({ value: t, label: t });
    }
    return opts;
  }, [lessonStart]);

  const returnTimeOptions = useMemo(() => {
    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    /*
    // change request by client 2026-01-12
    const toMinutes = (t: string) => {
      if (!t || !/^\d{2}:\d{2}$/.test(t)) return 0;
      const [hh, mm] = t.split(':').map(Number);
      return hh * 60 + mm;
    };
    */
    const format = (mins: number) =>
      `${pad(Math.floor(mins / 60))}:${pad(mins % 60)}`;

    const END_DAY_MIN = 22 * 60; // 22:00
    /*
    // change request by client 2026-01-12
    // Use lessonStart instead of lessonEnd as the floor.
    // This prevents the selector from "locking" to late times if a late return time was previously set.
    const startMin =
      typeof lessonStart === 'string' && /^\d{2}:\d{2}$/.test(lessonStart)
        ? toMinutes(lessonStart)
        : 7 * 60;
    */

    // Show full list of return times (07:00 - 22:00) as requested.
    // change request by client 2026-01-12
    const startMin = 7 * 60;

    if (startMin > END_DAY_MIN) return [];

    const opts: Array<{ value: string; label: string }> = [];
    for (let m = startMin; m <= END_DAY_MIN; m += 15) {
      const t = format(m);
      opts.push({ value: t, label: t });
    }
    return opts;
  }, []);

  const carOptions = useMemo(() => {
    const items = (carListData as any)?.items ?? [];
    return items.map((it: any) => ({
      value: it.id,
      label: it.name
    }));
  }, [carListData]);

  const updateTransportSchema = useMemo(() => updateTransportModalSchema, []);
  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting }
  } = useForm<UpdateTransportFormValues>({
    resolver: zodResolver(updateTransportSchema),
    mode: 'onChange',
    defaultValues: {
      car_id: (data as any)?.lesson?.car?.id,
      pickup_time: data.lesson.pickup_time || '',
      return_time: data.lesson.pickup_end_time || '',
      transport_comments: data.lesson.transport_comments || '',
      transport_pax: data.lesson.transport_pax || 0,
      start: rosterSelectedDate
    }
  });

  const pickup = watch('pickup_time');
  const transportPax = watch('transport_pax');
  const selectedCarId = watch('car_id');
  // const disableReturn = !pickup;

  // Duplicate booking state
  const [showDuplicateBookingModal, setShowDuplicateBookingModal] =
    useState(false);
  const lastCarAvailabilityParams = useRef<string>('');

  const handleDuplicateBookingContinue = useCallback(() => {
    setShowDuplicateBookingModal(false);
  }, []);

  // Check car availability when car, pickup time, and date are present
  useEffect(() => {
    const selectedDate =
      rosterSelectedDate || toYyyyMmDd(data.lesson.start_time);
    if (!selectedDate || !pickup || !selectedCarId) return;

    const defaultCarId = (data as any)?.lesson?.car?.id;
    const defaultPickupTime = data.lesson.pickup_time;

    if (
      defaultCarId &&
      defaultCarId === selectedCarId &&
      defaultPickupTime &&
      defaultPickupTime === pickup
    ) {
      return;
    }

    const scheduleId = (modalConfig?.data as any)?.lesson?.id as
      | number
      | undefined;
    const params = new URLSearchParams({
      pickup_time: String(pickup),
      start_date: String(selectedDate),
      end_date: String(selectedDate),
      car_id: String(selectedCarId)
    });
    if (scheduleId) params.append('schedule_id', String(scheduleId));

    const queryString = params.toString();
    if (lastCarAvailabilityParams.current === queryString) return;
    lastCarAvailabilityParams.current = queryString;

    let cancelled = false;
    (async () => {
      try {
        const resp = await checkCarAvailabilityMutation(queryString);
        if (!cancelled) {
          if (resp?.success && (resp?.data as any)?.status) {
            setShowDuplicateBookingModal(true);
          }
        }
      } catch {
        // ignore errors; availability check is advisory
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    pickup,
    selectedCarId,
    rosterSelectedDate,
    data.lesson.start_time,
    modalConfig?.data,
    transportPax,
    checkCarAvailabilityMutation
  ]);

  const onSubmit = async (values: UpdateTransportFormValues) => {
    // delegate to modalConfig.onClick for submit handling
    if (!data?.lesson?.id) return;
    const response = await updateTransportRosterMutation({
      data: values as unknown as Record<string, unknown>,
      id: data?.lesson?.id
    });
    if (response?.success) {
      hideCommonModal();
      await queryClient.invalidateQueries({
        queryKey: ['transport-roster', `date=${rosterSelectedDate}`]
      });
    }
  };

  // filteredReturnOptions removed.

  if (isInstructor) {
    return <InstructorTransportView data={data} />;
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className='space-y-4 w-full max-w-full px-4 md:px-6 pb-4 md:pb-6'
    >
      <CustomConfirmModal
        show={showDuplicateBookingModal}
        setShow={setShowDuplicateBookingModal}
        data={{
          title: 'Duplicate vehicle booking alert!',
          message:
            'Time slot is already booked for selected vehicle. Please choose another vehicle or continue',
          onClick: handleDuplicateBookingContinue
        }}
      />

      <div className='flex items-center justify-between mb-2'>
        <h2 className='text-lg md:text-xl font-bold text-gray-800'>
          Update Transport
        </h2>
      </div>

      <div className='overflow-x-auto'>
        <table className='table-fixed w-full border border-gray-300 rounded-xl text-sm text-center mx-auto mb-4 min-w-[1100px]'>
          <colgroup>
            <col style={{ width: '180px' }} />
            <col style={{ width: '150px' }} />
            <col style={{ width: '70px' }} />
            <col style={{ width: '220px' }} />
            <col style={{ width: '120px' }} />
            <col style={{ width: '150px' }} />
            <col style={{ width: '150px' }} />
            <col style={{ width: '120px' }} />
            <col style={{ width: '120px' }} />
          </colgroup>
          <thead className='bg-blue-100 text-gray-800'>
            <tr>
              <th className='py-2 px-2.5 border'>Instructor</th>
              <th className='py-2 px-2.5 border'>Client</th>
              <th className='py-2 px-2.5 border'># Party</th>
              <th className='py-2 px-2.5 border'>Hotel</th>
              <th className='py-2 px-2.5 border'>Mountain</th>
              <th className='py-2 px-2.5 border'>
                Vehicle <span className='text-red-500'>*</span>
              </th>
              <th className='py-2 px-2.5 border'>Pick-up numbers</th>
              <th className='py-2 px-2.5 border'>
                Pickup Time<span className='text-red-500'>*</span>
              </th>
              <th className='py-2 px-2.5 border'>
                Return Time<span className='text-red-500'>*</span>
              </th>
            </tr>
          </thead>
          <tbody className='bg-white divide-y divide-gray-200'>
            <tr className='bg-white hover:bg-gray-50'>
              <td className='border px-2 py-1.5 align-middle'>
                <div className='px-2 py-1.5 bg-gray-50 rounded border text-center break-words whitespace-normal'>
                  {data.lesson.instructor_name}
                </div>
              </td>
              <td className='border px-2 py-1.5 align-middle'>
                <div className='px-2 py-1.5 bg-gray-50 rounded border text-center break-words whitespace-normal'>
                  {data.lesson.client_name}
                </div>
              </td>
              <td className='border px-2 py-1.5 align-middle'>
                <div className='px-2 py-1.5 bg-gray-50 rounded border text-center'>
                  {data.lesson.number_in_party}
                </div>
              </td>
              <td className='border px-2 py-1.5 align-middle'>
                <div className='px-2 py-1.5 bg-gray-50 rounded border min-h-10 min-w-10 text-center break-words whitespace-normal'>
                  {data?.lesson?.hotel?.name}
                </div>
                <span className='block mt-1 text-xs text-slate-600'>
                  {data?.lesson?.hotel?.google_map_link && (
                    <a
                      href={data?.lesson?.hotel?.google_map_link}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-blue-600 underline flex items-center justify-center gap-1'
                    >
                      Google Map Link
                      <Link size={15} />
                    </a>
                  )}
                </span>
              </td>
              <td className='border px-2 py-1.5 align-middle'>
                <div className='px-2 py-1.5 bg-gray-50 rounded border min-h-10 min-w-10 text-center break-words whitespace-normal'>
                  {data?.lesson?.mountain?.name}
                </div>
              </td>
              <td className='border px-2 py-1.5 align-middle'>
                <FormSelect
                  name='car_id'
                  control={control}
                  options={carOptions}
                  placeholder='Select vehicle'
                />
              </td>
              <td className='border px-2 py-1.5 align-middle'>
                <FormSelect
                  name='transport_pax'
                  control={control}
                  options={CreateArrayOptions(100, 'Select Pick-up numbers')}
                  placeholder='Select Pick-up numbers'
                />
              </td>
              <td className='border px-2 py-1.5 align-middle'>
                <FormSelect
                  name='pickup_time'
                  control={control}
                  options={pickupTimeOptions}
                  placeholder='Select time'
                />
              </td>
              <td className='border px-2 py-1.5 align-middle'>
                <FormSelect
                  name='return_time'
                  control={control}
                  options={returnTimeOptions}
                  placeholder='Select time'
                  disabled={false}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className='overflow-x-auto'>
        <table className='table-fixed w-full border border-gray-300 rounded-xl text-sm text-center mx-auto mb-4'>
          <colgroup>
            <col style={{ width: '100%' }} />
          </colgroup>
          <thead className='bg-blue-100 text-gray-800'>
            <tr>
              <th className='py-2 px-2.5 border'>Comment</th>
            </tr>
          </thead>
          <tbody className='bg-white divide-y divide-gray-200'>
            <tr className='bg-white hover:bg-gray-50'>
              <td className='border px-2 py-1.5 align-middle'>
                <FormTextarea
                  name='transport_comments'
                  control={control}
                  label=''
                  maxLength={500}
                  characterCount={(watch('transport_comments') || '').length}
                  className={'border-1'}
                  row={1}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {!isInstructor && (
        <div className='flex flex-wrap justify-end gap-3 pt-4'>
          <button
            type='button'
            onClick={() => hideCommonModal()}
            className='px-5 py-2.5 bg-white text-gray-800 rounded-lg border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-300 min-w-[96px]'
          >
            Close
          </button>
          <button
            type='submit'
            disabled={isSubmitting}
            className='px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-600 min-w-[140px]'
          >
            Update Transport
          </button>
        </div>
      )}
    </form>
  );
};

export default UpdateTransportModal;
